import type { User } from '../types/user';
import { ApiError } from './client';
import { AUTH_BASE_URL } from './config';
import {
  BadResponseError,
  NetworkError,
  ORGANIZATION_INACTIVE_CODE,
  OrganizationInactiveError,
  TimeoutError
} from './errors';

// Re-exported so callers keep importing the auth surface from one place.
export { ORGANIZATION_INACTIVE_CODE, OrganizationInactiveError };

interface AuthMeResponse {
  data?: { user?: User };
  type?: string;
  detail?: string;
}

/**
 * Without this the request never settles when the auth host black-holes it and
 * `checkAuth` reports nothing at all — a silent failure mode sitting on top of
 * the loud one. Uses AbortController rather than `AbortSignal.timeout` so it
 * still works on Safari < 16.
 */
const AUTH_ME_TIMEOUT_MS = 10_000;

export const fetchUser = async (): Promise<User | null> => {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, AUTH_ME_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(`${AUTH_BASE_URL}/auth/me`, {
      credentials: 'include',
      signal: controller.signal
    });
  } catch (cause) {
    // Native fetch rejects with a bare TypeError for DNS / TLS / offline / CORS.
    // It carries no status, so unless it is wrapped here the caller cannot tell
    // a transport failure from an HTTP one — which is how every such failure
    // came to be reported under the useless error code "TypeError".
    throw timedOut
      ? new TimeoutError('Timed out reaching the auth service')
      : new NetworkError('Could not reach the auth service', cause);
  } finally {
    clearTimeout(timer);
  }

  // `res.ok` was previously unchecked, which made every error status — including
  // the 403 for a deactivated organization — read as a plain "logged out" and
  // bounce the user to login with no explanation.
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as AuthMeResponse;

    if (res.status === 403 && body.type?.endsWith(ORGANIZATION_INACTIVE_CODE)) {
      throw new OrganizationInactiveError(
        body.detail ??
          'Your organization no longer has access to the partner portal.',
        body.detail
      );
    }

    // 401 is the ordinary unauthenticated case: no session, no error screen.
    if (res.status === 401) {
      return null;
    }

    // Carries the real status instead of interpolating it into a message
    // string, so a 500 outage and a 404 stay distinguishable downstream.
    throw new ApiError(
      body.detail ?? `Failed to fetch the current user (${res.status})`,
      res.status,
      body.detail,
      body.type
    );
  }

  const data = (await res.json().catch(() => null)) as AuthMeResponse | null;
  if (data === null) {
    throw new BadResponseError('Malformed /auth/me response');
  }

  return data.data?.user ?? null;
};
